import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useNavigate } from "react-router";
import { surSessionExpiree } from "./api/client";
import { cles, useSession } from "./api/hooks";
import { Layout } from "./composants/Layout";
import { ChantierFiche } from "./ecrans/ChantierFiche";
import { Chantiers } from "./ecrans/Chantiers";
import { Clients } from "./ecrans/Clients";
import { Connexion } from "./ecrans/Connexion";
import { ContenuAvis } from "./ecrans/ContenuAvis";
import { ContenuCommunes } from "./ecrans/ContenuCommunes";
import { ContenuFaq } from "./ecrans/ContenuFaq";
import { ContenuPrestations } from "./ecrans/ContenuPrestations";
import { ContenuRealisations } from "./ecrans/ContenuRealisations";
import { LeadFiche } from "./ecrans/LeadFiche";
import { Leads } from "./ecrans/Leads";
import { Medias } from "./ecrans/Medias";
import { Publication } from "./ecrans/Publication";
import { Reglages } from "./ecrans/Reglages";
import { Seo } from "./ecrans/Seo";
import { TableauDeBord } from "./ecrans/TableauDeBord";

/**
 * Écran d'attente pendant la vérification de session. Il existe pour une seule
 * raison : sans lui, l'application afficherait une fraction de seconde de
 * contenu protégé avant de savoir si quelqu'un est connecté.
 */
function EcranChargement({ libelle }: { libelle: string }) {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      <CircularProgress aria-hidden />
      {/* `aria-live` fait annoncer l'attente par les lecteurs d'écran, qui ne
          « voient » pas un cercle qui tourne. */}
      <Typography aria-live="polite" color="text.secondary">
        {libelle}
      </Typography>
    </Box>
  );
}

/**
 * Barrière d'authentification. Tant que `GET /api/auth/me` n'a pas répondu,
 * rien du contenu protégé n'est rendu — ni en clair, ni caché en CSS.
 */
function Protege() {
  const { data: utilisateur, isPending, isError, error } = useSession();

  if (isPending) return <EcranChargement libelle="Vérification de votre session…" />;

  // Une panne de l'API n'est pas une absence de session : la confondre avec
  // une déconnexion enverrait l'utilisateur remplir un formulaire qui
  // échouerait tout autant.
  if (isError) {
    return (
      <Box sx={{ p: 4, maxWidth: 640, mx: "auto" }}>
        <Alert severity="error">
          L'administration ne parvient pas à joindre l'API. {error.message}
        </Alert>
      </Box>
    );
  }

  if (!utilisateur) return <Navigate to="/connexion" replace />;

  return (
    <Layout utilisateur={utilisateur}>
      <Outlet />
    </Layout>
  );
}

export function App() {
  const naviguer = useNavigate();
  const cache = useQueryClient();

  useEffect(() => {
    // Le client HTTP ne connaît pas le routeur. On lui prête ici de quoi
    // ramener l'utilisateur à la connexion sans recharger la page entière,
    // et on invalide la session pour que la barrière ci-dessus reparte d'un
    // état honnête plutôt que d'un cache périmé.
    surSessionExpiree(() => {
      cache.setQueryData(cles.session, null);
      naviguer("/connexion", { replace: true });
    });

    return () => surSessionExpiree(null);
  }, [naviguer, cache]);

  return (
    <Routes>
      <Route path="/connexion" element={<Connexion />} />
      <Route element={<Protege />}>
        <Route path="/" element={<TableauDeBord />} />

        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/:id" element={<LeadFiche />} />

        <Route path="/clients" element={<Clients />} />
        <Route path="/chantiers" element={<Chantiers />} />
        <Route path="/chantiers/:id" element={<ChantierFiche />} />

        <Route path="/contenu/prestations" element={<ContenuPrestations />} />
        <Route path="/contenu/communes" element={<ContenuCommunes />} />
        <Route path="/contenu/realisations" element={<ContenuRealisations />} />
        <Route path="/contenu/faq" element={<ContenuFaq />} />
        <Route path="/contenu/avis" element={<ContenuAvis />} />
        <Route path="/seo" element={<Seo />} />
        <Route path="/reglages" element={<Reglages />} />
        <Route path="/medias" element={<Medias />} />
        <Route path="/publication" element={<Publication />} />
        {/* Une URL inconnue ramène au tableau de bord, mais seulement une fois
            la session vérifiée : la redirection reste sous la barrière. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
